import { snapshotComparePluginKey } from '@tiptap-pro/extension-snapshot-compare';
import type { Editor } from '@tiptap/core';
import { isHistoryTransaction } from '@tiptap/pm/history';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { ReplaceAroundStep, ReplaceStep } from '@tiptap/pm/transform';

import type { Reference } from '@/lib/zybooks-api';
import { getOrCreateReferenceStore } from '../reference-extension/reference-store';

import { TABLE_MOVE_ROW_COLUMN_META_KEY } from '@/components/tiptap-editor/tiptap-node/table-node/lib/tiptap-table-utils';

/** Transaction meta: replay after user confirmed delete of a protected node. */
export const PROTECTED_NODE_BYPASS_META_KEY = 'protectedNodeBypass';

export const protectedNodePluginKey = new PluginKey('protectedNode');

/** One protected block that would be fully removed by the transaction, in order encountered. */
export interface ProtectedNodeDeletionInfo {
    typeName: string;
}

export interface ReferencedResourceDeletionInfo {
    typeName: string;
    nodeId: string;
    inboundReferences: Reference[];
}

export interface ProtectedNodeDeletePromptContext {
    editor: Editor;
    transaction: Transaction;
    /** Protected node types that are fully removed (document order, duplicates allowed if multiple blocks). */
    protectedNodesToDelete: ProtectedNodeDeletionInfo[];
    /** Referenced resources fully removed by this transaction. */
    referencedNodesToDelete: ReferencedResourceDeletionInfo[];
    resolve: (confirmed: boolean) => void;
}

export interface ProtectedNodePluginOptions {
    protectedNodeTypes: string[];
    getEditor: () => Editor;
    onProtectedDeletePrompt: (ctx: ProtectedNodeDeletePromptContext) => void;
}

function getDeletedRanges(step: ReplaceStep | ReplaceAroundStep): { from: number; to: number }[] {
    if (step instanceof ReplaceStep) {
        return [{ from: step.from, to: step.to }];
    }
    if (step instanceof ReplaceAroundStep) {
        const ranges: { from: number; to: number }[] = [];
        if (step.from < step.gapFrom) {
            ranges.push({ from: step.from, to: step.gapFrom });
        }
        if (step.gapTo < step.to) {
            ranges.push({ from: step.gapTo, to: step.to });
        }
        return ranges;
    }
    return [];
}

/**
 * Protected node whose document span is exactly `[from, to)` (typical single-node replace).
 */
function findExactSpanningProtectedNode(doc: PMNode, from: number, to: number, protectedTypes: ReadonlySet<string>): PMNode | undefined {
    let found: PMNode | undefined;
    doc.nodesBetween(from, to, (node, pos) => {
        if (!protectedTypes.has(node.type.name)) {
            return;
        }
        if (pos === from && pos + node.nodeSize === to) {
            found = node;
            return false;
        }
    });
    return found;
}

/**
 * True when a ReplaceStep keeps the protected block in place rather than deleting it:
 * - same-type replace with unchanged inner content (`setNodeMarkup` / attribute-only update)
 * - wrap in a single parent that still contains that exact node (e.g. CommentsKit `blockThread`
 *   via `replaceWith` when commenting on an atom block like zyAnimator)
 */
function isProtectedNodePreservingReplace(
    doc: PMNode,
    step: ReplaceStep,
    range: { from: number; to: number },
    protectedTypes: ReadonlySet<string>,
): boolean {
    const { from, to } = range;
    if (from >= to) {
        return false;
    }

    const oldNode = findExactSpanningProtectedNode(doc, from, to, protectedTypes);
    if (!oldNode) {
        return false;
    }

    const { slice } = step;
    if (slice.content.childCount !== 1) {
        return false;
    }
    const inserted = slice.content.child(0);
    if (inserted.type === oldNode.type) {
        return inserted.content.eq(oldNode.content);
    }
    return inserted.childCount === 1 && inserted.child(0).eq(oldNode);
}

/**
 * True when a ReplaceStep keeps a referenced node (by id) rather than deleting it:
 * attribute-only same-type replace, or wrap in a parent that still contains that exact node.
 */
function isReferencedNodePreservingReplace(doc: PMNode, step: ReplaceStep, range: { from: number; to: number }, nodeId: string): boolean {
    const { from, to } = range;
    if (from >= to || step.slice.content.childCount !== 1) {
        return false;
    }

    const inserted = step.slice.content.child(0);
    let oldNode: PMNode | undefined;
    doc.nodesBetween(from, to, (candidate, candidatePos) => {
        if (String(candidate.attrs?.id ?? '') !== nodeId) {
            return;
        }
        if (candidatePos === from && candidatePos + candidate.nodeSize === to) {
            oldNode = candidate;
            return false;
        }
    });
    if (!oldNode) {
        return false;
    }

    if (inserted.type === oldNode.type && String(inserted.attrs?.id ?? '') === nodeId && inserted.content.eq(oldNode.content)) {
        return true;
    }
    return inserted.childCount === 1 && inserted.child(0).eq(oldNode);
}

export function getReferencedNodesDeletedInTransaction(
    startDoc: PMNode,
    tr: Transaction,
    referencedNodesById: Record<string, { isReferenced: boolean; inboundReferences: Reference[] }>,
): ReferencedResourceDeletionInfo[] {
    const out: ReferencedResourceDeletionInfo[] = [];
    const seenNodeIds = new Set<string>();
    let doc: PMNode = startDoc;

    for (const step of tr.steps) {
        if (step instanceof ReplaceStep || step instanceof ReplaceAroundStep) {
            for (const range of getDeletedRanges(step)) {
                doc.nodesBetween(range.from, range.to, (node, pos) => {
                    const nodeId = String(node.attrs?.id ?? '');
                    if (!nodeId) {
                        return;
                    }
                    if (seenNodeIds.has(nodeId)) {
                        return;
                    }
                    if (pos < range.from || pos + node.nodeSize > range.to) {
                        return;
                    }

                    const referenceMeta = referencedNodesById[nodeId];
                    if (!referenceMeta?.isReferenced) {
                        return;
                    }

                    if (step instanceof ReplaceStep && isReferencedNodePreservingReplace(doc, step, range, nodeId)) {
                        return;
                    }

                    seenNodeIds.add(nodeId);
                    out.push({
                        typeName: node.type.name,
                        nodeId,
                        inboundReferences: referenceMeta.inboundReferences,
                    });
                });
            }
        }

        const result = step.apply(doc);
        if (result.failed) {
            break;
        }
        doc = result.doc!;
    }

    return out;
}

/**
 * Lists every protected node that would be fully removed by the transaction, in
 * pre-step order (per step, document order with `nodesBetween`), simulated doc per step.
 *
 * Full-node same-type replaces with unchanged inner content (e.g. `setNodeMarkup` from
 * `updateAttributes`) and comment wraps that preserve the node (e.g. `blockThread`) are not
 * included.
 */
export function getProtectedNodesDeletedInTransaction(
    startDoc: PMNode,
    tr: Transaction,
    protectedTypes: ReadonlySet<string>,
): ProtectedNodeDeletionInfo[] {
    const out: ProtectedNodeDeletionInfo[] = [];
    let doc: PMNode = startDoc;
    for (const step of tr.steps) {
        if (step instanceof ReplaceStep || step instanceof ReplaceAroundStep) {
            for (const range of getDeletedRanges(step)) {
                if (step instanceof ReplaceStep && isProtectedNodePreservingReplace(doc, step, range, protectedTypes)) {
                    continue;
                }
                doc.nodesBetween(range.from, range.to, (node, pos) => {
                    if (!protectedTypes.has(node.type.name)) {
                        return;
                    }
                    if (pos >= range.from && pos + node.nodeSize <= range.to) {
                        out.push({ typeName: node.type.name });
                    }
                });
            }
        }
        const result = step.apply(doc);
        if (result.failed) {
            break;
        }
        doc = result.doc!;
    }
    return out;
}

export function createProtectedNodePlugin(options: ProtectedNodePluginOptions): Plugin {
    const { protectedNodeTypes, getEditor, onProtectedDeletePrompt } = options;
    const protectedSet = new Set(protectedNodeTypes);

    let latestBlocked: {
        tr: Transaction;
        protectedNodesToDelete: ProtectedNodeDeletionInfo[];
        referencedNodesToDelete: ReferencedResourceDeletionInfo[];
        resolve: (confirmed: boolean) => void;
    } | null = null;

    let flushScheduled = false;

    const flushPrompt = () => {
        flushScheduled = false;
        const lb = latestBlocked;
        if (!lb) {
            return;
        }
        const editor = getEditor();
        if (!editor?.view) {
            latestBlocked = null;
            return;
        }
        onProtectedDeletePrompt({
            editor,
            transaction: lb.tr,
            protectedNodesToDelete: lb.protectedNodesToDelete,
            referencedNodesToDelete: lb.referencedNodesToDelete,
            resolve: lb.resolve,
        });
    };

    return new Plugin({
        key: protectedNodePluginKey,

        filterTransaction(tr, state) {
            if (!tr.docChanged || tr.steps.length === 0) {
                return true;
            }
            if (
                tr.getMeta('y-sync$') ||
                tr.getMeta('__uniqueIDTransaction') ||
                tr.getMeta('zyversion') ||
                tr.getMeta('uiEvent') === 'drop' ||
                tr.getMeta(TABLE_MOVE_ROW_COLUMN_META_KEY) ||
                isHistoryTransaction(tr) ||
                tr.getMeta(snapshotComparePluginKey) !== undefined
            ) {
                return true;
            }
            if (tr.getMeta(PROTECTED_NODE_BYPASS_META_KEY)) {
                return true;
            }

            const protectedNodesToDelete = getProtectedNodesDeletedInTransaction(state.doc, tr, protectedSet);
            const editor = getEditor();
            const referencedNodesToDelete = getReferencedNodesDeletedInTransaction(
                state.doc,
                tr,
                editor ? getOrCreateReferenceStore(editor).getState().referencedNodesById : {},
            );

            if (protectedNodesToDelete.length === 0 && referencedNodesToDelete.length === 0) {
                return true;
            }

            const blockedTr = tr;
            const resolve = (confirmed: boolean) => {
                if (!latestBlocked || latestBlocked.tr !== blockedTr) {
                    return;
                }
                latestBlocked = null;
                if (!confirmed) {
                    return;
                }
                const editor = getEditor();
                if (!editor?.view) {
                    return;
                }
                if (!blockedTr.before.eq(editor.state.doc)) {
                    return;
                }
                editor.view.dispatch(blockedTr.setMeta(PROTECTED_NODE_BYPASS_META_KEY, true));
            };

            latestBlocked = { tr: blockedTr, protectedNodesToDelete, referencedNodesToDelete, resolve };

            if (!flushScheduled) {
                flushScheduled = true;
                queueMicrotask(flushPrompt);
            }

            return false;
        },
    });
}
